import { withFormValidation } from '@updater-components/form-element';
import {
  withTextComponent,
  withTemplate,
  applyAsAttributes,
} from '@updater-legacy/components/custom-element';
import masks from './masks';
import styles from './index.css';

const OBSERVED_ATTRIBUTES = [
  'disabled',
  'inputmode',
  'mask',
  'maxlength',
  'minlength',
  'name',
  'pattern',
  'placeholder',
  'readonly',
  'required',
  'type',
  'value',
  'novalidate',
  'autocomplete',
];

const html = '<input class="upd-input__input" type="text" />';

class Input extends withFormValidation(
  withTemplate(html)(withTextComponent())
) {
  static observedAttributes = OBSERVED_ATTRIBUTES;
  static reflectedAttributes = OBSERVED_ATTRIBUTES.concat(['aria-disabled']);
  static attributeTypes = {
    disabled: 'boolean',
    readonly: 'boolean',
    required: 'boolean',
  };

  constructor() {
    super();
  }

  connected() {
    const template = document.createElement('template');
    template.innerHTML = `<style>${styles}</style>`;
    const style = template.content.cloneNode(true);

    this.appendChild(style);
    this.appendChild(this._template);
    this._input = this.querySelector('input');
    this._input.addEventListener('focus', this._handleFocus);
    this._input.addEventListener('blur', this._handleFocus);
    this._input.addEventListener('invalid', this._handleRedispatch);
    this._input.addEventListener('change', this._handleRedispatch);
    this._input.addEventListener('input', this._handleInput);
    this._input.addEventListener('blur', this._handleBlur);
    this.addEventListener('click', this._handleClick);
  }

  disconnected() {
    this._input.removeEventListener('focus', this._handleFocus);
    this._input.removeEventListener('blur', this._handleFocus);
    this._input.removeEventListener('input', this._handleInput);
    this._input.removeEventListener('invalid', this._handleRedispatch);
    this._input.removeEventListener('change', this._handleRedispatch);
    this._input.removeEventListener('blur', this._handleBlur);
    this.removeEventListener('click', this._handleClick);
  }

  updated({
    disabled,
    inputmode,
    mask,
    maxlength,
    minlength,
    name,
    pattern,
    placeholder,
    readonly,
    required,
    type,
    value,
    autocomplete,
  }) {
    this.ariaDisabled = disabled;

    applyAsAttributes(this._input, {
      disabled,
      inputmode,
      name,
      maxlength,
      minlength,
      pattern,
      placeholder: placeholder || this._currentMask.placeholder || null,
      readonly,
      required,
      autocomplete,
      type,
    });

    this._applyValue(value || '');
  }

  /**
   * Temporarily adds the requiredValidator if the input field is required.
   * The requiredValidator throws an error message when the input is whitespace only.
   * After the check is completed, we return the validators array to the original state
   * without the requiredValidator.
   *
   * @returns {*|boolean}
   */
  checkValidity(force) {
    if (this.novalidate && !force) {
      return true;
    }
    const temp = this._validators;
    if (this.required) {
      this._validators = this._validators.concat(requiredValidator);
    }
    const isValid = super.checkValidity();
    this._validators = temp;

    return isValid;
  }

  get _currentMask() {
    return masks[this.mask] || masks.noop;
  }

  focusInput() {
    this._input.focus();
  }

  _applyValue(value) {
    const maskedValue = this._currentMask.set(value);

    // Modifying an `<input>`'s `value` breaks `change` events in IE11/Edge.
    // https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/15181565/
    // To limit the impact of this bug, only touch the internal `input`'s
    // `value` if we have to.
    if (this._input.value !== maskedValue) {
      const previousCursorPosition = this._input.selectionStart;

      const cursorAtEnd =
        !this.previousMaskedValue ||
        previousCursorPosition === this.previousMaskedValue.length + 1;

      this._input.value = maskedValue;

      // Check if value is set since IE11 fires an `input` event
      // when the placeholder changes.
      if (value) {
        /*
         * TODO: Refactor this to put more logic inside the cursorPosition
         * functions. Right now, cursorPosition basically returns the end of
         * the masked value. Instead, it should be smarter about where the
         * user’s previous cursor was. The logic here does that naively (it
         * preserves the cursor position if it wasn’t at the end). This results
         * in some bugs when typing over a mask character, but it’s an
         * improvement on previous behavior, which always moved the cursor to
         * the end.
         */
        if (cursorAtEnd) {
          const cursorPos =
            this._currentMask.cursorPosition &&
            this._currentMask.cursorPosition(value);
          if (cursorPos) {
            this._setCursorPosition(cursorPos);
          }
        } else {
          this._setCursorPosition(previousCursorPosition);
        }
      }
    }

    this.previousMaskedValue = maskedValue;
  }

  _setCursorPosition(cursorPos) {
    // setTimeout needed for Android when entering with onscreen keyboard
    setTimeout(() => {
      this._input.focus();
      this._input.setSelectionRange(cursorPos, cursorPos);
    });
  }

  _handleFocus = ({ type }) => {
    type === 'focus'
      ? this.classList.add('upd-input-focus')
      : this.classList.remove('upd-input-focus');
  };

  _handleInput = (event) => {
    const {
      target: { value },
    } = event;
    const unmaskedValue = this._currentMask.get(value);

    if (this.value !== unmaskedValue) {
      // If `value` changed, the mask will be applied in `updated`...
      this.value = unmaskedValue;
    } else {
      // ...otherwise, we apply the mask here.
      this._applyValue(value);
    }

    this._handleRedispatch(event);

    if (this._hasBeenValidatedAlready) {
      this.checkValidity();
    }
  };

  // TODO - revisiting form control validations
  // moving _handleBlur into with-form-validation mixin
  // or other reusable place for consistent behaviour across form controls
  _handleBlur = () => {
    if (this.value) {
      this.checkValidity();
    }
  };

  _handleClick = () => {
    this.focusInput();
  };

  _handleRedispatch = (event) => {
    // 1. The `invalid` event does not bubble so we re-dispatch it to mimic the
    // native checkbox functionality
    // 2. Re-dispatch the `change` and `input` events to allow for composition.
    // 3. We pass the input's `value` on the event's `detail` property since it
    // isn't possible to read `target.value` across shadow boundaries.
    event.stopPropagation();

    this.dispatchEvent(
      new CustomEvent(event.type, {
        bubbles: true,
        composed: true,
        detail: this.value,
      })
    );
  };
}

customElements.define('upd-input', Input);

function requiredValidator(element) {
  const { value } = element;
  const isValid = !value || !value.match(/^\s+$/);
  element.setCustomValidity(isValid ? '' : `This field is required`);
  return isValid;
}
